import React, { useEffect, useMemo, useState } from 'react';
import {
  Snackbar as MUISnackbar,
  SnackbarContent
} from '@mui/material';
import * as R from 'ramda';

import { Button, Icon, IconButton } from '@mui';
import { colors } from '@constants/colors';
import '@shared/visually-hidden-legend.scss';

const EMPTY_NOTIFICATION = {
  type: 'info',
  message: ''
};

// Forces a screen reader to re-announce two consecutive, identical messages - most ATs only
// announce a live region when its text actually changes, so an unchanged string is silently
// ignored otherwise.
const ZERO_WIDTH_SPACE = '​';

// Minimum/maximum time (ms) a success/info toast stays up, plus extra time per character so
// longer messages get more reading time before auto-dismissing. Errors never use this - see
// `resolvedAutoHideDuration` below - a missed error has worse consequences than a missed
// confirmation, so they stay up until manually dismissed regardless of what a caller passes.
const MIN_AUTO_HIDE_DURATION = 6000;
const MAX_AUTO_HIDE_DURATION = 20000;
const MS_PER_CHARACTER = 50;

const getAutoHideDuration = message =>
  Math.min(MIN_AUTO_HIDE_DURATION + (message.length * MS_PER_CHARACTER), MAX_AUTO_HIDE_DURATION);

const styles = {
  icon: {
    marginRight: '1rem',
    color: colors.neutral.white
  },
  button: {
    color: colors.neutral.white,
    minWidth: 0
  },
  info: {
    backgroundColor: colors.neutral.dark
  },
  error: {
    backgroundColor: colors.utility.error
  }
};

const icons = {
  info: null,
  warning: <Icon style={styles.icon} aria-hidden="true">warning</Icon>,
  error: <Icon style={styles.icon} aria-hidden="true">error</Icon>
};

export class SnackbarWrapper {
  constructor() {
    this.notifications = [];
    this.subscribers = [];
    this.nextId = 0;
  }

  subscribe = subscriber => {
    this.subscribers = [...this.subscribers, subscriber];
  };

  notify = () => {
    this.subscribers.forEach(subscriber => subscriber(this.notifications));
  };

  getNotificationHandler = type => (notification) => {
    this.nextId += 1;
    this.notifications = [...this.notifications, { ...notification, type, id: this.nextId }];
    this.notify();
  };

  handleExit = () => {
    this.notifications = R.remove(0, 1, this.notifications);
    this.notify();
  };

  info = this.getNotificationHandler('info');
  warning = this.getNotificationHandler('warning');
  error = this.getNotificationHandler('error');

  dismiss = () => {
    this.notifications = [];
    this.notify();
  };

  Provider = () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [open, setOpen] = useState(false);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [notifications, setNotifications] = useState([]);
    // Permanent, visually-hidden announcers - mounted empty from app start, never cleared when
    // the visible toast closes. The visible card below carries no role/aria-live of its own
    // (see SnackbarContent) so these are the only things that ever announce a message; dismissing
    // or auto-hiding the visible card can no longer cut an announcement off mid-speech.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [politeAnnouncement, setPoliteAnnouncement] = useState('');
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [assertiveAnnouncement, setAssertiveAnnouncement] = useState('');

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      this.subscribe(setNotifications);

      return () => {
        this.subscribers = [];
      };
    }, []);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      // keeps peeling notifications off the front of the stack until 1 remains
      // this ensures component is properly opened and closed instead of the content being instantly replaced
      // setting open to false causes MUI Snackbar to call onExited which in turn removes the first notification
      // from the stack causing this effect to be run again, looping until 1 remains
      if (notifications.length >= 2) {
        setOpen(false);
      } else if (notifications.length === 1) {
        setOpen(true);
      }
    }, [notifications]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const notification = useMemo(() => {
      return R.pathOr(EMPTY_NOTIFICATION, [0], notifications);
    }, [notifications]);

    const {
      id,
      type,
      message = '',
      autoHideDuration,
      action,
      disableClickAwayDismiss,
      onActionClick = () => {},
      onDismiss = () => {}
    } = notification;

    // Announce each notification exactly once, in order, as it becomes the current one - keyed
    // on `id` (not `message`) so two consecutive identical messages each still announce.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (!message) {
        return;
      }
      const setAnnouncement = type === 'error' ? setAssertiveAnnouncement : setPoliteAnnouncement;
      setAnnouncement(previous => (previous === message ? `${message}${ZERO_WIDTH_SPACE}` : message));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Errors stay up until manually dismissed regardless of what a caller passes - a missed
    // error has worse consequences than a missed confirmation. Success/info respect an explicit
    // autoHideDuration from the caller, otherwise scale with message length.
    const resolvedAutoHideDuration = type === 'error'
      ? null
      : (autoHideDuration ?? getAutoHideDuration(message));

    const handleClose = (event, reason) => {
      if (!disableClickAwayDismiss || reason !== 'clickaway') {
        setOpen(false);
        onDismiss();
      }
    };

    const handleDismiss = () => {
      setOpen(false);
      onDismiss();
    };

    const handleActionClick = () => {
      setOpen(false);
      onActionClick();
    };

    return (
      <>
        <span styleName="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
          {politeAnnouncement}
        </span>
        <span styleName="visually-hidden" role="alert" aria-live="assertive" aria-atomic="true">
          {assertiveAnnouncement}
        </span>
        <MUISnackbar
          open={open}
          autoHideDuration={resolvedAutoHideDuration}
          onClose={handleClose}
          TransitionProps={{
            onExited: this.handleExit
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <SnackbarContent
            role="presentation"
            message={
              <>
                {icons[type]}
                {message}
              </>
            }
            action={
              <>
                <Button
                  onClick={handleActionClick}
                  size="small"
                  style={styles.button}
                >
                  {action || 'Dismiss'}
                </Button>
                {action && (
                  <IconButton size="small" onClick={handleDismiss} aria-label="Close">
                    <Icon color={colors.neutral.white} aria-hidden="true">close</Icon>
                  </IconButton>
                )}
              </>
            }
            style={type === 'error' ? styles.error : styles.info}
          />
        </MUISnackbar>
      </>
    );
  };
}

export const Snackbar = new SnackbarWrapper();
